// localStorage-backed persistence for the dashboard: selected server, cached
// login, and per-guild voice targets. Kept in one place so App.jsx stays lean.

const SELECTED_SERVER_KEY = 'botboard:selected-server-id';
const USER_KEY = 'botboard:user';

export function savedUser() {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveUser(user) {
  if (user) window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  else window.localStorage.removeItem(USER_KEY);
}

export function savedServer() {
  try {
    const raw = window.localStorage.getItem(SELECTED_SERVER_KEY);
    if (!raw) return null;
    if (raw.startsWith('{')) return JSON.parse(raw);
    if (/^\d+$/.test(raw)) {
      return { id: raw, name: 'Selected server', members: null };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveServer(server) {
  window.localStorage.setItem(SELECTED_SERVER_KEY, JSON.stringify(server));
}

// Per-bot voice target ('auto' or a channel id), persisted per guild so a
// browser refresh keeps the selection instead of snapping back to Auto.
export function savedVoiceTargets(guildId) {
  try {
    const raw = window.localStorage.getItem(`botboard:voice-targets:${guildId}`);
    return { sound: 'auto', music: 'auto', ...(raw ? JSON.parse(raw) : {}) };
  } catch {
    return { sound: 'auto', music: 'auto' };
  }
}

export function saveVoiceTargets(guildId, targets) {
  try {
    window.localStorage.setItem(`botboard:voice-targets:${guildId}`, JSON.stringify(targets));
  } catch {}
}

// Registered bot modules (manifest + status) — cached so extra bots like
// PatchWatcher appear immediately on refresh without waiting for the first poll.
const MODULES_KEY = 'botboard:modules';

export function savedModules() {
  try {
    const raw = window.localStorage.getItem(MODULES_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveModules(modules) {
  try {
    window.localStorage.setItem(MODULES_KEY, JSON.stringify(modules));
  } catch {}
}

export function savedModuleOrder(guildId) {
  try {
    const raw = window.localStorage.getItem(`botboard:module-order:${guildId}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveModuleOrder(guildId, order) {
  try {
    window.localStorage.setItem(`botboard:module-order:${guildId}`, JSON.stringify(order));
  } catch {}
}

// Bot identity (names + avatar URLs) — cached so the first render after a
// refresh shows the correct names/avatars without waiting for the status poll.
const BOT_INFO_KEY = 'botboard:bot-info';

export function savedBotInfo() {
  try {
    const raw = window.localStorage.getItem(BOT_INFO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveBotInfo(info) {
  try {
    window.localStorage.setItem(BOT_INFO_KEY, JSON.stringify(info));
  } catch {}
}

// Cleared on logout / when landing on the login screen, so a new login always
// starts back on Auto (a fixed pick only survives refreshes within a session).
export function clearVoiceTargets() {
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('botboard:voice-targets:')) window.localStorage.removeItem(key);
    }
  } catch {}
}
